import React from 'react';
import {
  Document,
  PDFViewer,
  Page as PDFPage,
  Text as PDFText,
  View as PDFView,
  Image as PDFImage
} from '@react-pdf/renderer';
import { getCurrentDateTime } from '../../../../../utils';
import { useLocation, useParams } from 'react-router-dom';

const styles = {
  page: {
    padding: 20
  },
  heading: {
    fontSize: 18,
    textAlign: 'center',
    marginBottom: 20
  },
  departmentHeading: {
    fontSize: 14,
    fontWeight: 'bold',
    marginVertical: 10
  },
  contentContainer: {
    flexDirection: 'row',
    marginLeft: '30%',
    marginTop: 0,
    marginBottom: 0,
    border: '1px Solid black',
    padding: '10px'
  },
  row: {
    flexDirection: 'row',
    marginBottom: 0,
    alignItems: 'center',
    width: '100%'
  },
  text: {
    fontSize: 8,
    display: 'inline-block'
  },

  columnHeader: {
    width: '100%',
    backgroundColor: '#1976D2',
    color: 'white',
    padding: 5,
    textAlign: 'center'
  },
  columnContent: {
    width: '17%',
    padding: 5,
    textAlign: 'center'
  }
};

const fullScreenStyles = {
  container: {
    position: 'fixed',
    top: 0,
    left: 0,
    width: '100vw',
    height: '100vh',
    zIndex: 1000,
    backgroundColor: 'white'
  },
  column: {
    width: '50%',
    paddingRight: '10px',
    justifyContent: 'center',
    marginLeft: '10%',
    marginBottom: '5%'
  }
};

const Page = ({ style, children, ...props }) => (
  <PDFPage style={{ ...styles.page, ...style }} {...props}>
    {children}
  </PDFPage>
);

const View = ({ style, children, ...props }) => (
  <PDFView style={{ ...style }} {...props}>
    {children}
  </PDFView>
);

const Text = ({ style, content, ...props }) => (
  <PDFText style={{ ...styles.text, ...style }} {...props}>
    {content}
  </PDFText>
);

const COLORS = ['#f0f0f0', '#e0e0e0'];

const formatDate = (dateString) => {
  const date = new Date(dateString);
  const day = date.getDate().toString().padStart(2, '0');
  const month = (date.getMonth() + 1).toString().padStart(2, '0');
  const year = date.getFullYear();
  return `${day}-${month}-${year}`;
};

export const OpdPatientLedgerPdf = ({ data }) => {
  console.log('Opd Ledger Data', data);

  let combinedTotal = 0;

  data.forEach((item) => {
    combinedTotal += item.opd.total;
    item.billing_details.forEach((bill) => {
      combinedTotal += bill.total_amt;
    });
  });

  return (
    <div style={fullScreenStyles.container}>
      <PDFViewer width="100%" height="100%" showToolbar>
        <Document title="Opd Patient Ledger Report">
          <Page style={styles.page} size="A4" orientation="portrait">
            <PDFImage
              src="/logo_Hospital_m.jpeg"
              style={{ width: '100%', height: '80px', marginBottom: 1, marginRight: '10%' }}
            />
            <Text
              content="Opd Consolidated Bill"
              style={{ fontSize: '12px', textAlign: 'center', marginBottom: '10px' }}
            />
            {data &&
              data.map((item, index) => (
                <View key={index} style={styles.row}>
                  {/* First Column */}
                  <View style={fullScreenStyles.column}>
                    <Text content={`Opd No.                :       ${item.opd.op_no}`} />
                    <Text
                      content={`Patient Name       :        ${item.opd.name_suffix} ${item.opd.first_name} ${item.opd.last_name}`}
                    />
                    <Text content={`Address                :       ${item.opd.address.address}`} />
                  </View>

                  {/* Second Column */}
                  <View style={fullScreenStyles.column}>
                    <Text
                      content={`Age                  :       ${new Date().getFullYear() - new Date(item.opd.dob).getFullYear()}`}
                    />
                    <Text content={`Gender            :        ${item.opd.gender}`} />
                    <Text content={`Mobile No.        :       ${item.opd.address.mobile}`} />
                  </View>
                </View>
              ))}

            {/* Table Header */}
            <View style={styles.row}>
              <Text style={styles.columnHeader} content="Sr. No." />
              <Text style={styles.columnHeader} content="Bill Date" />
              <Text style={styles.columnHeader} content="Bill No." />
              <Text style={styles.columnHeader} content="Doctor" />
              <Text style={styles.columnHeader} content="Service" />
              <Text style={styles.columnHeader} content="Net Amount" />
            </View>

            <Text style={{ fontSize: '10px' }} content="Opd Consultancy" />
            {data &&
              data.map((item, index) => (
                <View key={index} style={{ ...styles.row, backgroundColor: COLORS[index % COLORS.length] }}>
                  <Text style={styles.columnContent} content={index + 1} />
                  <Text style={styles.columnContent} content={formatDate(item.opd.date)} />
                  <Text style={styles.columnContent} content="" />
                  <Text style={styles.columnContent} content={item.opd.consultant.name} />
                  <Text style={styles.columnContent} content="OPD" />
                  <Text style={styles.columnContent} content={item?.opd?.total} />
                </View>
              ))}

            <Text style={{ fontSize: '10px' }} content="Opd Billing" />
            {data &&
              data.map(
                (item, index) =>
                  item.billing_details &&
                  item.billing_details.map((bill, billIndex) => {
                    const serviceItems = Array.isArray(bill.service_items) ? bill.service_items : [bill.service_items]; // Ensure service_items is treated as an array

                    return serviceItems.map((service, serviceIndex) => (
                      <View
                        key={`${billIndex}-${serviceIndex}`}
                        style={{
                          ...styles.row,
                          backgroundColor: COLORS[(billIndex + serviceIndex) % COLORS.length]
                        }}
                      >
                        <Text style={styles.columnContent} content={billIndex + 1} />
                        <Text style={styles.columnContent} content={formatDate(bill.date)} />
                        <Text style={styles.columnContent} content={bill.bill_no} />
                        <Text style={styles.columnContent} content={service.doctor} />
                        <Text style={styles.columnContent} content={service.service_name} />
                        <Text style={styles.columnContent} content={service.net_amt} />
                      </View>
                    ));
                  })
              )}

            <View style={{ ...styles.row, marginTop: 30 }}>
              <Text
                style={{ ...styles.columnContent, fontWeight: 'bold', marginLeft: '70%' }}
                content="Grand Total :"
              />
              <Text style={{ ...styles.columnContent, fontWeight: 'bold' }} content={`${combinedTotal.toFixed(2)}`} />
            </View>
          </Page>
        </Document>
      </PDFViewer>
    </div>
  );
};

export default OpdPatientLedgerPdf;
